/* eslint-disable react-refresh/only-export-components -- imperative module: exports showBanner/dismissBanner helpers alongside an internal card component; not an HMR boundary. */
import type { KeyboardEvent } from 'react';
import { ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { ImgWithFallback } from './img-with-fallback';

/**
 * A generic, tappable arrival banner built on top of sonner's `toast.custom`.
 *
 * It reuses the app's already-mounted `<Toaster />` (no new portal/provider) and
 * knows nothing about any feature domain, so any caller can surface one: an
 * overlapping avatar stack, a two-line text block, and a chevron affordance, all
 * inside a single tap target that also swipes away like a normal toast.
 */
export interface BannerAvatar {
  src?: string;
  initials: string;
}

export interface BannerOptions {
  /** Stable id. Passing the same id replaces the banner in place; omit or vary to stack. */
  id?: string | number;
  /** Overlapping avatar stack. Up to 3 render; any extra are implied with a +N chip. */
  avatars?: BannerAvatar[];
  /** Primary line, e.g. "Samuel, George and Eduardo can help". Truncates to one line. */
  title: string;
  /** Secondary line, e.g. the question title. Truncates to one line. */
  subtitle?: string;
  /** Runs on tap; the banner also dismisses itself afterwards. */
  onClick: () => void;
  /** Auto-dismiss delay in milliseconds (default 8000). */
  durationMs?: number;
  /** Runs when the banner is taken down: a tap, a swipe, or `dismissBanner`. */
  onDismiss?: () => void;
  /** Runs when the banner closes on its own after `durationMs`. */
  onAutoClose?: () => void;
}

const DEFAULT_DURATION_MS = 8000;
const MAX_VISIBLE_AVATARS = 3;

interface BannerCardProps {
  avatars?: BannerAvatar[];
  title: string;
  subtitle?: string;
  /** Fired on tap or keyboard activation; wired to run onClick then dismiss the toast. */
  onActivate: () => void;
}

/**
 * The visual banner. Rendered as a `role="button"` div (not a native `<button>`)
 * on purpose: sonner suppresses its swipe-to-dismiss gesture when a pointer press
 * lands on a `<button>` element, so a div-with-button-role keeps both the tap and
 * the swipe working. Keyboard activation (Enter/Space) is wired up for parity.
 */
function BannerCard({ avatars, title, subtitle, onActivate }: BannerCardProps) {
  const visibleAvatars = avatars?.slice(0, MAX_VISIBLE_AVATARS) ?? [];
  const overflowCount = (avatars?.length ?? 0) - visibleAvatars.length;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.repeat) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onActivate();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={title}
      onClick={onActivate}
      onKeyDown={handleKeyDown}
      className="flex w-full cursor-pointer select-none items-center gap-3 rounded-xl p-3 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-gray-900/20 active:bg-gray-100 [@media(hover:hover)]:hover:bg-gray-50"
    >
      {visibleAvatars.length > 0 && (
        <div className="flex shrink-0 items-center -space-x-2">
          {visibleAvatars.map((avatar, index) => (
            <ImgWithFallback
              key={index}
              src={avatar.src}
              alt=""
              className="size-8 rounded-full object-cover ring-2 ring-white"
              fallback={() => (
                <span
                  aria-hidden="true"
                  className="flex size-8 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-600 ring-2 ring-white"
                >
                  {avatar.initials}
                </span>
              )}
            />
          ))}
          {overflowCount > 0 && (
            <span className="flex size-8 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-500 ring-2 ring-white">
              +{overflowCount}
            </span>
          )}
        </div>
      )}

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold leading-tight text-gray-900">{title}</p>
        {subtitle && (
          <p className="mt-0.5 truncate text-xs leading-tight text-gray-500">{subtitle}</p>
        )}
      </div>

      <ChevronRight className="size-5 shrink-0 text-gray-400" aria-hidden="true" />
    </div>
  );
}

/**
 * Show a tappable banner at the top of the screen. Returns the sonner toast id.
 *
 * Tapping runs `onClick` and then dismisses the banner. Passing the same `id`
 * replaces the current banner in place; a different or absent id stacks.
 */
export function showBanner(opts: BannerOptions): string | number {
  return toast.custom(
    id => (
      <BannerCard
        avatars={opts.avatars}
        title={opts.title}
        subtitle={opts.subtitle}
        onActivate={() => {
          opts.onClick();
          toast.dismiss(id);
        }}
      />
    ),
    {
      duration: opts.durationMs ?? DEFAULT_DURATION_MS,
      // Fill the toast row so the banner spans the full toast width on every device.
      className: 'w-full',
      // Only set id when provided: sonner spreads this options object last, so an
      // explicit `id: undefined` would clobber the auto-generated id and desync it
      // from the id handed to the jsx callback (breaking tap-to-dismiss).
      ...(opts.id !== undefined && { id: opts.id }),
      // Likewise only when provided: an update in place merges over the toast, so an explicit
      // undefined would clear the callback an earlier showing set.
      ...(opts.onDismiss && { onDismiss: opts.onDismiss }),
      ...(opts.onAutoClose && { onAutoClose: opts.onAutoClose }),
    },
  );
}

/** Dismiss a banner (or any toast) by the id returned from {@link showBanner}. */
export function dismissBanner(id: string | number): void {
  toast.dismiss(id);
}
